'use client';

import { c as _c } from "react/compiler-runtime";
import { useQueries } from '@morfi/backend-sdk-v2/client';
import { logger } from '@/lib/telemetry/logger';
import { getConnection } from '@/lib/tx-engine/connection';
import { type OutstandingTrade, useTradeSubmissionLockStore } from '@/stores/trade-submission-lock';
import { useTxSubmissionStore } from '@/stores/tx-submission';
const loggedReconciliationFailures: Set<string> = new Set();
const UNKNOWN_TRADE_LOCK_TIMEOUT_MS: number = 5 * 60 * 1000;
export function useUnknownTradeStatus() {
  const $ = _c(4);
  const outstanding = useTradeSubmissionLockStore(_temp);
  let t0;
  if ($[0] !== outstanding) {
    t0 = outstanding.flatMap(_temp2);
    $[0] = outstanding;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      queries: t0
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useQueries(t1);
}
function _temp2(trade) {
  return trade.status === "unknown" ? [{
    queryKey: ["unknown-trade-status", trade.network, trade.id],
    queryFn: () => reconcileUnknownTrade(trade),
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
    retry: false
  }] : [];
}
function _temp(s) {
  return s.outstanding;
}
export async function reconcileUnknownTrade(trade: OutstandingTrade): Promise<boolean> {
  const entry = trade.traceId === undefined ? undefined : useTxSubmissionStore.getState().submissions[trade.traceId];
  if (entry?.stage === 'confirmed') {
    useTradeSubmissionLockStore.getState().finish(trade.id);
    return true;
  }
  if (entry?.stage === 'cancelled') {
    useTradeSubmissionLockStore.getState().finish(trade.id);
    logger.warn('trade.cancelled_submission_lock_cleared', {
      submissionId: trade.id,
      traceId: trade.traceId ?? null
    });
    return true;
  }
  const signature = trade.signature ?? entry?.signature;
  if (signature === '') {
    useTradeSubmissionLockStore.getState().finish(trade.id);
    logger.warn('trade.empty_signature_lock_cleared', {
      submissionId: trade.id,
      traceId: trade.traceId ?? null
    });
    return true;
  }
  if (signature === undefined) {
    return isRecoveryWindowExpired(trade) ? finishExpiredTrade(trade) : false;
  }
  try {
    const response = await getConnection(trade.network).getSignatureStatuses([signature], {
      searchTransactionHistory: true
    });
    const status = response.value[0];
    if (status == null || !['confirmed', 'finalized'].includes(status.confirmationStatus ?? '')) {
      return isRecoveryWindowExpired(trade) ? finishExpiredTrade(trade) : false;
    }
    useTradeSubmissionLockStore.getState().finish(trade.id);
    return true;
  } catch {
    if (!loggedReconciliationFailures.has(trade.id)) {
      loggedReconciliationFailures.add(trade.id);
      logger.warn('trade.status_reconciliation_failed', {
        submissionId: trade.id,
        traceId: trade.traceId ?? null
      });
    }
    return false;
  }
}
function isRecoveryWindowExpired(trade: OutstandingTrade): boolean {
  if (trade.unknownAt === undefined) return true;
  return Date.now() - trade.unknownAt >= UNKNOWN_TRADE_LOCK_TIMEOUT_MS;
}
function finishExpiredTrade(trade: OutstandingTrade): true {
  useTradeSubmissionLockStore.getState().finish(trade.id);
  logger.warn('trade.submission_lock_expired', {
    submissionId: trade.id,
    traceId: trade.traceId ?? null,
    hasSignature: trade.signature !== undefined && trade.signature.length > 0,
    ageMs: trade.unknownAt === undefined ? null : Math.max(0, Date.now() - trade.unknownAt)
  });
  return true;
}